/**
 * GIF search, backed by Klipy (https://klipy.com).
 *
 * Tenor was retired - its v1 API now answers every request with
 * 403 "Tenor API is discontinued", which is why the picker had gone blank.
 * Klipy's /v2 endpoints are deliberately Tenor-v2-compatible, so the response
 * already arrives in the shape this picker expects: { results, next }, with
 * each result carrying `media_formats.tinygif` etc.
 *
 * Note the old code read `media[0].tinygif.url` - that was the Tenor *v1*
 * shape. v2 (and therefore Klipy) exposes `media_formats.tinygif.url`, an
 * object rather than an array, so the callers were updated to match.
 */

const KLIPY_BASE = "https://api.klipy.com/v2";

const API_KEY = process.env.REACT_APP_KLIPY_API_KEY || "";

export interface GifSearchResponse {
  results: any[];
  /** opaque cursor to pass back as `pos` for the next page; empty when exhausted */
  next: string;
}

const EMPTY: GifSearchResponse = { results: [], next: "" };

/**
 * Search Klipy for GIFs, or fetch the featured set when no query is given.
 *
 * @param query    search term; empty string fetches the featured/trending feed
 * @param page     opaque cursor from the previous response's `next` ("" for first page)
 * @param perPage  results per page
 * @param whenResult callback invoked with { results, next } - always called, even
 *                   on failure (with an empty result) so the picker can stop its spinner
 */
async function SearchGif(
  query = "",
  page = "",
  perPage = 30,
  whenResult: (response: GifSearchResponse) => void = () => {}
) {
  if (!API_KEY) {
    console.warn(
      "[gif] REACT_APP_KLIPY_API_KEY is not set - GIF search is disabled."
    );
    whenResult(EMPTY);
    return;
  }

  const params = new URLSearchParams({
    key: API_KEY,
    limit: String(perPage),
  });
  // `pos` is Klipy/Tenor's cursor. Only send it when we actually have one -
  // omitting it is what asks for the first page.
  if (page) {
    params.set("pos", page);
  }

  // no query => the featured feed (Klipy's equivalent of Tenor's trending;
  // /v2/trending exists but comes back empty, /v2/featured is the live one)
  let url: string;
  if (query && query.trim().length > 0) {
    params.set("q", query.trim());
    url = `${KLIPY_BASE}/search?${params.toString()}`;
  } else {
    url = `${KLIPY_BASE}/featured?${params.toString()}`;
  }

  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.error(`[gif] Klipy responded ${response.status}`);
      whenResult(EMPTY);
      return;
    }
    const json = await response.json();
    whenResult({
      results: Array.isArray(json?.results) ? json.results : [],
      next: json?.next || "",
    });
  } catch (error) {
    console.error("[gif] Klipy request failed", error);
    whenResult(EMPTY);
  }
}

/**
 * Both helpers tolerate the v2 (`media_formats`) and legacy v1 (`media[0]`)
 * shapes, so a caller can't break just because the provider moved.
 */
function getFormats(gif: any): any {
  return gif?.media_formats || gif?.media?.[0];
}

/** Small thumbnail - what the picker grid renders. */
export function getGifPreviewUrl(gif: any): string {
  const formats = getFormats(gif);
  return (
    formats?.tinygif?.url ||
    formats?.nanogif?.url ||
    formats?.mediumgif?.url ||
    formats?.gif?.url ||
    ""
  );
}

/** Full-size GIF - what gets attached to a post/message. */
export function getGifFullUrl(gif: any): string {
  const formats = getFormats(gif);
  return (
    formats?.gif?.url ||
    formats?.mediumgif?.url ||
    formats?.tinygif?.url ||
    ""
  );
}

export default SearchGif;
